import DataMap from 'assets/components/dataMap/DataMap';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import { isNil } from 'lodash';
import { KeyNames, KeyTypeNames, useRefundConfigTemplateProvider } from '../RefundConfigTemplate.Hooks';

export default function RefundConfigTemplateEdit() {
  const { lang } = getLocales();
  const {
    selected,
    pageRouter,
    saveApi,
    masterTemplatesSelectItems,
    productsSelectionItems,
    refundTypesSelectItems,
    insurersSelectionItems,
    surchargesSelectionItems,
    syncBy,
    upsertKeyFD,
    safeArr,
    hasOption,
  } = useRefundConfigTemplateProvider();

  return (
    <>
      <ModalForm<Api.Core.RefundConfigTemplate.Save.IRequest>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[selected]}
        title={lang.refundConfigTemplate}
        default={selected?.toJSON()}
        onCancel={pageRouter.updateParams}
        onSubmit={(data) => saveApi.execute(data)}
        validation={(data, errors) => {
          if (!data?.internalName) errors.internalName = lang.mustNotBeEmpty;
          if (!data?.products?.length) errors.products = { _objectError: lang.mustNotBeEmpty };
          if ((data?.cancellationPeriodThresholdDays ?? 0) <= 0) {
            errors.cancellationPeriodThresholdDays = `${lang.mustNotBeEmpty}, ${lang.mustBeMoreThanAmount} 0`;
          }
          if ((data?.dealerProtectProfitExpirationDays ?? 0) <= 0) {
            errors.dealerProtectProfitExpirationDays = `${lang.mustNotBeEmpty}, ${lang.mustBeMoreThanAmount} 0`;
          }

          safeArr(masterTemplatesSelectItems).forEach((mt) => {
            const mk = String(mt.id);
            const dict = (data?.masterTemplatesRefundType ?? {}) as Record<string, string>;
            const selectedType = dict[mk] ?? '';
            const inputName = KeyTypeNames.MASTER_KEY_TYPE + mt.id;
            if (!selectedType) errors[inputName] = lang.mustNotBeEmpty;
          });

          safeArr(insurersSelectionItems).forEach((it) => {
            const key = String(it.id);
            const dict = (data?.insurersRefundType ?? {}) as Record<string, string>;
            const selectedType = dict[key] ?? '';
            const inputName = KeyTypeNames.INSURER_KEY_TYPE + it.id;
            if (!selectedType) errors[inputName] = lang.mustNotBeEmpty;
          });

          safeArr(surchargesSelectionItems).forEach((it) => {
            const key = String(it.id);
            const dict = (data?.surchargesRefundType ?? {}) as Record<string, string>;
            const selectedType = dict[key] ?? '';
            const inputName = KeyTypeNames.SURCHARGE_KEY_TYPE + it.id;
            if (!selectedType) errors[inputName] = lang.mustNotBeEmpty;
          });
        }}
        render={({ form }) => {
          const nextMaster = syncBy(
            form.data?.masterTemplatesRefundType as Record<string, string> | undefined,
            masterTemplatesSelectItems,
            'id'
          );
          const nextInsurer = syncBy(
            form.data?.insurersRefundType as Record<string, string> | undefined,
            insurersSelectionItems,
            'id'
          );
          const nextSurcharge = syncBy(
            form.data?.surchargesRefundType as Record<string, string> | undefined,
            surchargesSelectionItems,
            'id'
          );

          if (
            nextMaster !== form.data?.masterTemplatesRefundType ||
            nextInsurer !== form.data?.insurersRefundType ||
            nextSurcharge !== form.data?.surchargesRefundType
          ) {
            form.update({
              masterTemplatesRefundType: nextMaster,
              insurersRefundType: nextInsurer,
              surchargesRefundType: nextSurcharge,
            });
          }

          return (
            <>
              <FormWrapper>
                <TextInput
                  name="internalName"
                  label={lang.name}
                  value={form.data?.internalName}
                  error={form.errors?.internalName}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <SelectInput
                  name="products"
                  label={lang.products}
                  data={productsSelectionItems}
                  isMultiselect
                  value={form.data?.products?.map((it) => it.id)}
                  error={form.errors?.products?._objectError}
                  onChange={({ products: idList }) =>
                    form.update({
                      products: idList.map(
                        (id: number) => form.data?.products?.find((it) => it.id === id) ?? { id }
                      ),
                    })
                  }
                  icon={<Icon class="fas-clipboard-list" />}
                />
                <CheckInput
                  name="isEnabled"
                  label={lang.isEnabled}
                  value={form.data?.isEnabled}
                  onChange={form.update}
                  class={inputStyles.check.switch}
                  htmlElementProps={{ title: form.data?.isEnabled ? lang.disable : lang.enable }}
                />
                <NumberInput
                  name="cancellationPeriodThresholdDays"
                  label={lang.cancellationPeriodThresholdDays}
                  value={form.data?.cancellationPeriodThresholdDays}
                  error={form.errors?.cancellationPeriodThresholdDays}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <CheckInput
                  name="isLienHolderApplicable"
                  label={lang.isLienHolderApplicable}
                  value={form.data?.isLienHolderApplicable}
                  onChange={form.update}
                  class={inputStyles.check.switch}
                  htmlElementProps={{ title: form.data?.isLienHolderApplicable ? lang.disable : lang.enable }}
                />
                <NumberInput
                  name="minimumRetainedPremium"
                  label={lang.minimumRetainedPremium}
                  value={
                    !isNil(form.data?.minimumRetainedPremium)
                      ? Number((form.data?.minimumRetainedPremium * 100).toFixed(2))
                      : undefined
                  }
                  onChange={({ minimumRetainedPremium }) =>
                    form.update({
                      minimumRetainedPremium: !isNil(minimumRetainedPremium)
                        ? minimumRetainedPremium * 0.01
                        : undefined,
                    })
                  }
                  icon={<Icon class="fas-percent" />}
                />
                <CheckInput
                  name="prorateRemainingTimeOrKmAtRisk"
                  label={lang.prorateRemainingTimeOrKmAtRisk}
                  value={form.data?.prorateRemainingTimeOrKmAtRisk}
                  onChange={form.update}
                  class={inputStyles.check.switch}
                  disabled={!form.data?.minimumRetainedPremium}
                  htmlElementProps={{
                    title: form.data?.prorateRemainingTimeOrKmAtRisk ? lang.disable : lang.enable,
                  }}
                />
                <CheckInput
                  name="requiresCurrentOdometerReading"
                  label={lang.requiresCurrentOdometerReading}
                  value={form.data?.requiresCurrentOdometerReading}
                  onChange={form.update}
                  class={inputStyles.check.switch}
                  htmlElementProps={{ title: form.data?.requiresCurrentOdometerReading ? lang.disable : lang.enable }}
                />
                <CheckInput
                  name="maximumKmApplies"
                  label={lang.maximumKmApplies}
                  value={form.data?.maximumKmApplies}
                  onChange={form.update}
                  class={inputStyles.check.switch}
                  htmlElementProps={{ title: form.data?.maximumKmApplies ? lang.disable : lang.enable }}
                />
                <InputWrapper>
                  <NumberInput
                    name="administrationFee"
                    label={lang.administrationFee}
                    error={form.errors?.administrationFee}
                    value={form.data?.administrationFee}
                    onChange={form.update}
                    icon={<Icon class="if-quill-pen" />}
                  />
                  <NumberInput
                    name="administrationFeeTax"
                    label={lang.administrationFeeTaxRate}
                    error={form.errors?.administrationFeeTax}
                    value={Number(((form.data?.administrationFeeTax || 0) * 100).toFixed(3))}
                    onChange={({ administrationFeeTax }) =>
                      form.update({
                        administrationFeeTax: administrationFeeTax * 0.01,
                      })
                    }
                    readOnly={!form.data?.administrationFee}
                    min={0}
                    max={100}
                    step={0.001}
                    icon={<Icon class="fas-percent" />}
                  />
                </InputWrapper>

                <NumberInput
                  name="dealerProtectProfitAmount"
                  label={lang.dealerProtectProfitAmount}
                  value={form.data?.dealerProtectProfitAmount}
                  error={form.errors?.dealerProtectProfitAmount}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <NumberInput
                  name="dealerProtectProfitExpirationDays"
                  label={lang.dealerProtectProfitExpirationDays}
                  value={form.data?.dealerProtectProfitExpirationDays}
                  error={form.errors?.dealerProtectProfitExpirationDays}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <CheckInput
                  name="isCancellationLetterRequired"
                  label={lang.isCancellationLetterRequired}
                  value={form.data?.isCancellationLetterRequired}
                  onChange={form.update}
                  class={inputStyles.check.switch}
                  htmlElementProps={{ title: form.data?.isCancellationLetterRequired ? lang.disable : lang.enable }}
                />
                <CheckInput
                  name="isInvoiceRequired"
                  label={lang.isInvoiceRequired}
                  value={form.data?.isInvoiceRequired}
                  onChange={form.update}
                  class={inputStyles.check.switch}
                  htmlElementProps={{ title: form.data?.isInvoiceRequired ? lang.disable : lang.enable }}
                />

                <InputWrapper>
                  <b>{lang.pricingDistributionMasterTemplates}</b>
                </InputWrapper>
                <DataMap
                  data={masterTemplatesSelectItems}
                  render={({ data }) => {
                    const key = String(data.id);
                    const refundList = safeArr(refundTypesSelectItems);
                    const name = KeyTypeNames.MASTER_KEY_TYPE + data.id;

                    const dict = (form.data?.masterTemplatesRefundType ?? {}) as Record<string, string>;
                    const currentType = dict[key] ?? '';
                    const isCurrentValid = currentType ? hasOption(refundList, currentType) : true;

                    return (
                      <InputWrapper key={data.id}>
                        <TextInput
                          name={KeyNames.MASTER_KEY + data.id}
                          label={lang.pricingDistributionMasterTemplate}
                          value={data.title}
                          readOnly
                          onChange={(v) => {
                            void v;
                          }}
                          icon={<Icon class="if-quill-pen" />}
                        />
                        <SelectInput
                          name={name}
                          label={lang.refundType}
                          data={refundTypesSelectItems}
                          value={isCurrentValid ? currentType : ''}
                          error={form.errors?.[name]}
                          onChange={(selectData) => {
                            const selectedType = (selectData as any)[name] as string;
                            form.update({
                              masterTemplatesRefundType: upsertKeyFD(
                                form.data?.masterTemplatesRefundType as Record<string, string> | undefined,
                                key,
                                selectedType
                              ),
                            });
                          }}
                          icon={<Icon class="fas-balance-scale" />}
                        />
                      </InputWrapper>
                    );
                  }}
                />

                <InputWrapper>
                  <b>{lang.insurers}</b>
                </InputWrapper>
                <DataMap
                  data={insurersSelectionItems}
                  render={({ data }) => {
                    const key = String(data.id);
                    const refundList = safeArr(refundTypesSelectItems);
                    const typeName = KeyTypeNames.INSURER_KEY_TYPE + data.id;

                    const dict = (form.data?.insurersRefundType ?? {}) as Record<string, string>;
                    const currentType = dict[key] ?? '';
                    const isCurrentValid = currentType ? hasOption(refundList, currentType) : true;

                    return (
                      <InputWrapper key={data.id}>
                        <TextInput
                          name={KeyNames.INSURER_KEY + data.id}
                          label={lang.insurer}
                          value={data.title}
                          readOnly
                          onChange={(v) => {
                            void v;
                          }}
                          icon={<Icon class="if-quill-pen" />}
                        />
                        <SelectInput
                          name={typeName}
                          label={lang.refundType}
                          data={refundTypesSelectItems}
                          value={isCurrentValid ? currentType : ''}
                          error={form.errors?.[typeName]}
                          onChange={(selectData) =>
                            form.update({
                              insurersRefundType: upsertKeyFD(
                                form.data?.insurersRefundType as Record<string, string> | undefined,
                                key,
                                (selectData as any)[typeName]
                              ),
                            })
                          }
                          icon={<Icon class="fas-balance-scale" />}
                        />
                      </InputWrapper>
                    );
                  }}
                />

                <InputWrapper>
                  <b>{lang.surcharges}</b>
                </InputWrapper>
                <DataMap
                  data={surchargesSelectionItems}
                  render={({ data }) => {
                    const key = String(data.id);
                    const refundList = safeArr(refundTypesSelectItems);
                    const typeName = KeyTypeNames.SURCHARGE_KEY_TYPE + data.id;

                    const dict = (form.data?.surchargesRefundType ?? {}) as Record<string, string>;
                    const currentType = dict[key] ?? '';
                    const isCurrentValid = currentType ? hasOption(refundList, currentType) : true;

                    return (
                      <InputWrapper key={data.id}>
                        <TextInput
                          name={KeyNames.SURCHARGE_KEY + data.id}
                          label={lang.surcharge}
                          value={data.title}
                          readOnly
                          onChange={(v) => {
                            void v;
                          }}
                          icon={<Icon class="if-quill-pen" />}
                        />
                        <SelectInput
                          name={typeName}
                          label={lang.refundType}
                          data={refundTypesSelectItems}
                          value={isCurrentValid ? currentType : ''}
                          error={form.errors?.[typeName]}
                          onChange={(selectData) =>
                            form.update({
                              surchargesRefundType: upsertKeyFD(
                                form.data?.surchargesRefundType as Record<string, string> | undefined,
                                key,
                                (selectData as any)[typeName]
                              ),
                            })
                          }
                          icon={<Icon class="fas-balance-scale" />}
                        />
                      </InputWrapper>
                    );
                  }}
                />
              </FormWrapper>
            </>
          );
        }}
      />
    </>
  );
}
